import { Box, Button, Card, Grid, TextField, Typography } from "@mui/material";
import { useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectGlobalDataState } from "../../../../features/global/globalDataSlice";
import { selectExchangeState, setExchanges } from "../../../../features/exchange/exchangeSlice";
import { useState } from "react";
import { sendGeneralRequest } from "../../../../features/generalRequest";
import CloseFullscreenOutlinedIcon from "@mui/icons-material/CloseFullscreenOutlined";
import colors from "../../../colors";
import { texts } from "../../../general/texts";
import ButtonGroup from "../../../general/ButtonGroup";
import CurrencyList from "./CurrencyList";
import LekStaticCurrency from "./LekStaticCurrency";
import SwapButton from "./SwapButton";
import MainView from "./MainView";
import FinalValueView from "./FinalValueView";
import HelpView from "./HelpView";

const Exchange = ({ classes }) => {
  const dispatch = useDispatch();
  const globalDataSlice = useSelector(selectGlobalDataState);
  const exchangeRatesSliceState = useSelector(selectExchangeState);
  const [moreInfo, setMoreInfo] = useState(false);
  const [data, setData] = useState(null);
  const memoizedData = useMemo(() => data, [data]);

  const searchMethods = [
    { display: "Konverto", value: "1" },
    { display: "Kontakto", value: "2" },
  ];

  const getSearchMethodValue = (value) => {
    setSearchMethod({ ...searchMethod, searchMethod: value });
  };

  const [searchMethod, setSearchMethod] = useState(searchMethods[0].value);
  const transactionType = {
    purchase: "purchase",
    sell: "sell",
  };

  const [state, setState] = useState({
    value: "",
    currency: { name: "", buysFor: "", sellsFor: "" },
    exchangeMethod: transactionType.purchase,
    finalValue: "",
  });

  const handleExchange = () => {
    state.exchangeMethod === transactionType.purchase
      ? setState({
          ...state,
          finalValue: (
            parseFloat(state.value) * parseFloat(state.currency.buysFor)
          )
            .toFixed(2)
            .replace(/\d(?=(\d{3})+\.)/g, "$&,"),
        })
      : setState({
          ...state,
          finalValue: (
            parseFloat(state.value) / parseFloat(state.currency.sellsFor)
          )
            .toFixed(2)
            .replace(/\d(?=(\d{3})+\.)/g, "$&,"),
        });
  };

  const handleCurrencyDropdownChange = (e) => {
    var deserializedValue = JSON.parse(e.target.value);
    setState({
      ...state,
      currency: {
        name: deserializedValue.name,
        buysFor: deserializedValue.buysFor,
        sellsFor: deserializedValue.sellsFor,
      },
    });
  };

  const handleTextFieldChange = (prop) => (e) => {
    setState({
      ...state,
      [prop]: e.target.value,
    });
  };

  const handleCurrencySwap = () => {
    state.exchangeMethod === transactionType.purchase
      ? setState({
          ...state,
          exchangeMethod: transactionType.sell,
          finalValue: (
            parseFloat(state.value) / parseFloat(state.currency.sellsFor)
          )
            .toFixed(2)
            .replace(/\d(?=(\d{3})+\.)/g, "$&,"),
        })
      : setState({
          ...state,
          exchangeMethod: transactionType.purchase,
          finalValue: (
            parseFloat(state.value) * parseFloat(state.currency.buysFor)
          )
            .toFixed(2)
            .replace(/\d(?=(\d{3})+\.)/g, "$&,"),
        });
  };

  useEffect(() => {
    dispatch(
      sendGeneralRequest({
        request: { token: globalDataSlice.token },
        baseUrlKey: "services",
        endpoint: "ShowExchange",
      })
    );
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      // Fetch new data
      dispatch(sendGeneralRequest({
        request: { token: globalDataSlice.token },
        baseUrlKey: 'services',
        endpoint: 'ShowExchange',
      }).then((newData) => {
        if (newData !== memoizedData) {
          // Data has changed, dispatch the action to update the store
          dispatch(setExchanges(newData));
          // Update the data state
          setData(newData);
        }
      }));
    }, 60000);

    return () => clearInterval(interval);
  }, [dispatch, memoizedData, globalDataSlice.token]);

  const selectedButtonStyle = {
    backgroundColor: colors.white,
    color: colors.darkBlue,
    width: "150px",
    height: "40px",
    fontFamily: "GerberaBold",
    textTransform: "none",
    fontSize: "1rem",
    border: "2px solid" + colors.darkBlue,
    borderRadius: "10px",
    "&:hover": {
      backgroundColor: colors.white,
    },
    "@media (max-width: 899px)": { width: "130px", height: "40px" },
  };

  const defaultButtonStyle = {
    backgroundColor: colors.white,
    width: "150px",
    height: "40px",
    borderRadius: "10px",
    textTransform: "none",
    fontFamily: "GerberaMedium",
    fontSize: "1rem",
    color: colors.darkBlue,
    "&:hover": {
      backgroundColor: colors.white,
    },
    "@media (max-width: 899px)": { width: "130px", height: "40px" },
  };

  return (
    <Grid
      container
      item
      mt={"0%"}
      md={12}
      xs={12}
      justifyContent={"right"}
      sx={{ "@media (max-width: 899px)": { justifyContent: "center" } }}
    >
      <Card className={classes.exchangeCard}  style={moreInfo === false ? { height: '90px' } : {}}>
        <Grid container mt={2} mb={2}>
          {moreInfo === false ? (
            <MainView 
              exchanges={exchangeRatesSliceState.exchanges}
              setMoreInfo={() => setMoreInfo(true)}
            />
          ) : (
            <>
              <Grid
                container
                item
                md={10}
                xs={10}
                justifyContent={"left"}
                alignItems={"center"}
                flexDirection={"center"}
                p={2}
              >
                <ButtonGroup
                  defaultButtonStyle={defaultButtonStyle}
                  selectedButtonStyle={selectedButtonStyle}
                  state={searchMethods}
                  sendValue={getSearchMethodValue}
                />
              </Grid>
              <Grid
                container
                item
                md={2}
                xs={2}
                justifyContent={"center"}
                alignItems={"center"}
                flexDirection={"center"}
              >
                <Button
                  onClick={() => setMoreInfo(false)}
                  disableRipple={true}
                  sx={{ ":hover": { background: colors.white } }}
                >
                  <Box
                    sx={{
                      width: "45px",
                      height: "45px",
                      background: colors.lightOrange,
                      borderRadius: "10px",
                    }}
                  >
                    <Grid
                      container
                      justifyContent={"center"}
                      alignItems={"center"}
                      flexDirection={"column"}
                      mt={1.3}
                    >
                      <CloseFullscreenOutlinedIcon color="warning" />
                    </Grid>
                  </Box>
                </Button>
              </Grid>
              {searchMethod.searchMethod === "1" && (
                <Grid
                  container
                  item
                  md={12}
                  xs={12}
                  justifyContent={"space-around"}
                  alignItems={"center"}
                  flexDirection={"center"}
                  padding={2}
                >
                  <Grid
                    container
                    item
                    md={12}
                    xs={12}
                    justifyContent={"center"}
                    alignItems={"center"}
                    columnSpacing={2}
                    rowSpacing={2}
                  >
                    <Grid container item md={12} xs={12}>
                      <TextField
                        value={state.value}
                        onChange={handleTextFieldChange("value")}
                        size="small"
                        label="Vlera"
                        fullWidth={true}
                        color={"warning"}
                      ></TextField>
                    </Grid>

                    {state.exchangeMethod === transactionType.purchase ? (
                      <CurrencyList
                        currencies={exchangeRatesSliceState.exchanges}
                        handleChange={(value) =>
                          handleCurrencyDropdownChange(value)
                        }
                        value={state.currency}
                      />
                    ) : (
                      <LekStaticCurrency colors={colors} />
                    )}

                    <SwapButton
                      handleCurrencySwap={handleCurrencySwap}
                      colors={colors}
                    />
                    {state.exchangeMethod === transactionType.sell ? (
                      <CurrencyList
                        currencies={exchangeRatesSliceState.exchanges}
                        handleChange={(value) =>
                          handleCurrencyDropdownChange(value)
                        }
                        value={state.currency}
                      />
                    ) : (
                      <LekStaticCurrency colors={colors} />
                    )}
                    <Grid container item md={12} xs={12}>
                      <Button
                        fullWidth={true}
                        disabled={
                          state.value === "" || state.currency.buysFor === ""
                            ? true
                            : false
                        }
                        sx={{
                          background: colors.orange,
                          color: colors.white,
                          fontFamily: "GerberaMedium",
                          textTransform: "none",
                          ":hover": { background: colors.orange },
                        }}
                        onClick={handleExchange}
                      >
                        {texts["button-convert"]}
                      </Button>
                    </Grid>
                    <FinalValueView
                      state={state}
                      transactionType={transactionType}
                    />
                  </Grid>
                </Grid>
              )}
              {searchMethod.searchMethod === "2" && <HelpView />}
            </>
          )}
        </Grid>
      </Card>
    </Grid>
  );
};

export default Exchange;
